import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dashboardAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ShoppingBag, Package, IndianRupee, CreditCard,
  ChevronRight, Bell, Truck, Scissors, FileCheck,
  CalendarDays, Clock, CheckCircle2,
} from 'lucide-react';
import { HelpTooltip } from '@/components/HelpTooltip';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardAPI.getData()
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-64" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}</div>
      <Skeleton className="h-20" />
    </div>
  );

  if (!data) return <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>Failed to load dashboard</p>;

  const s = data.stats || {};
  const now = new Date();
  const greeting = now.getHours() < 12 ? 'Good morning' : now.getHours() < 17 ? 'Good afternoon' : 'Good evening';

  // Pending actions — only items with count > 0
  const pendingActions = [
    { label: 'Unsent Messages', count: data.unsent_notifications || 0, icon: Bell, color: '#D97706', to: '/app/clients' },
    { label: 'Overdue Deliveries', count: s.orders_at_risk || data.pending_actions?.overdue_deliveries || 0, icon: Truck, color: '#DC2626', to: '/app/orders' },
    { label: 'QC Pending', count: data.pending_actions?.qc_pending || 0, icon: Scissors, color: '#7C3AED', to: '/app/production' },
    { label: 'Low Stock Trims', count: data.pending_actions?.low_stock_trims || 0, icon: Scissors, color: '#DC2626', to: '/app/trims' },
    { label: 'Invoices to Reconcile', count: data.pending_actions?.invoices_to_reconcile || 0, icon: FileCheck, color: '#2563EB', to: '/app/invoices/vendor' },
  ].filter(a => a.count > 0);

  const appointments = (data.today_appointments || []);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Row 1: Greeting */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: 'var(--brand-navy)' }}>{greeting}</h1>
          <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
            {now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <HelpTooltip text="Your daily snapshot. Click any card to take action." testId="help-dashboard" />
      </div>

      {/* Row 2: Four metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="metrics-row">
        <MetricCard
          icon={ShoppingBag} label="Active Orders" value={s.active_orders || 0}
          to="/app/orders" color="var(--brand-navy)" testId="metric-active-orders" />
        <MetricCard
          icon={Package} label="Awaiting Fabric" value={s.awaiting_fabric || 0}
          to="/app/invoices" color="#D97706" testId="metric-awaiting-fabric" />
        <MetricCard
          icon={IndianRupee} label="Revenue This Month"
          value={`\u20B9${(s.revenue_this_month || 0).toLocaleString('en-IN')}`}
          to="/app/finances" color="#059669" testId="metric-revenue" />
        <MetricCard
          icon={CreditCard} label="Outstanding Balances"
          value={`\u20B9${(s.outstanding_balances || 0).toLocaleString('en-IN')}`}
          to="/app/finances" color="#DC2626" testId="metric-outstanding" />
      </div>

      {/* Row 3: Pending Actions */}
      <div data-testid="pending-actions-row">
        {pendingActions.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-4 rounded-xl"
            style={{ backgroundColor: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.1)' }}
            data-testid="all-clear-message">
            <CheckCircle2 className="w-4 h-4" style={{ color: '#059669' }} />
            <span className="text-sm font-medium" style={{ color: '#059669' }}>All clear — no pending actions</span>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {pendingActions.map(action => {
              const Icon = action.icon;
              return (
                <Link key={action.label} to={action.to}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all hover:shadow-sm"
                  style={{ borderColor: action.color + '20', backgroundColor: action.color + '06' }}
                  data-testid={`pending-${action.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <Icon className="w-4 h-4" style={{ color: action.color }} />
                  <span className="text-sm font-semibold tabular-nums" style={{ color: action.color }}>{action.count}</span>
                  <span className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>{action.label}</span>
                  <ChevronRight className="w-3 h-3 opacity-40" />
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Row 4: Today's Appointments — only if count > 0 */}
      {appointments.length > 0 && (
        <Card className="rounded-xl border border-border/80 bg-white" data-testid="today-appointments-row">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4" style={{ color: '#2563EB' }} />
                <span className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--brand-navy)' }}>
                  Today's Appointments ({appointments.length})
                </span>
              </div>
              <Link to="/app/appointments" className="text-[10px] font-semibold hover:underline flex items-center gap-0.5"
                style={{ color: 'var(--brand-navy)' }}>
                View All <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="space-y-1">
              {appointments.slice(0, 6).map(apt => (
                <Link key={apt.id} to={`/app/appointments`}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-blue-50/50 transition-colors"
                  data-testid={`apt-${apt.id}`}>
                  <Clock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#2563EB' }} />
                  <span className="text-xs font-semibold tabular-nums w-14" style={{ color: '#2563EB' }}>{apt.start_time}</span>
                  <span className="text-sm font-medium flex-1 truncate" style={{ color: 'var(--navy-ink)' }}>
                    {apt.client_name || 'Walk-in'}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: 'rgba(37,99,235,0.08)', color: '#2563EB' }}>
                    {apt.appointment_type}
                  </span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* Clickable Metric Card */
function MetricCard({ icon: Icon, label, value, to, color, testId }) {
  const navigate = useNavigate();
  return (
    <Card
      className="rounded-xl bg-white border cursor-pointer transition-all hover:shadow-md group"
      onClick={() => navigate(to)}
      data-testid={testId}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '10' }}>
            <Icon className="w-4.5 h-4.5" style={{ color }} />
          </div>
          <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-40 transition-opacity" />
        </div>
        <p className="text-2xl font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>{value}</p>
        <p className="text-[10px] font-semibold uppercase tracking-wide mt-0.5" style={{ color: 'var(--ink-muted)' }}>{label}</p>
      </CardContent>
    </Card>
  );
}
